import React, { useContext, useState, useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import LightbulbCircleSharpIcon from '@mui/icons-material/LightbulbCircleSharp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LeafStateContext } from 'context/state/leaf';

import { BrightnessSlider } from 'components/light/BrightnessSlider';
import { LightsPowerSwitch } from 'components/light/PowerSwitch';
import {
  RGBColor,
  rgbToString,
  getColorProperties,
  createRgbGradient,
  createTempGradient,
  DEFAULT_COLOR,
  adjustTextColor,
} from 'modules/color';
import { LightState } from 'types/aspen/state';
import { ControlPanelWrapper } from '../AccordionDetailsContent/ComponentWrapper';

import styles from './lightsTile.module.scss';

// Light tile gradient constants
const POWER_OFF_GRADIENT =
  'linear-gradient(to bottom, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.15) 100%), radial-gradient(at top center, rgba(255,255,255,0.40) 0%, rgba(0,0,0,0.40) 120%) #989898';
const RGB_GRADIENT_TEMPLATE = (shadowColor: string, dimmedColor: string) =>
  `linear-gradient(to top, ${shadowColor} 50%, ${dimmedColor} 95%)`;
const TEMP_GRADIENT_TEMPLATE = (blendedColor: string, baseColor: string) =>
  `linear-gradient(to top, ${blendedColor} 50%, ${baseColor} 95%)`;

export const LightsTile = () => {
  const { leaf } = useContext(LeafStateContext);
  const leafState = leaf.state as LightState;
  const [isExpanded, setIsExpanded] = useState(false);

  const brightnessLevel = leafState.brightness?.level || 0;

  const colorProperties = useMemo(
    () => getColorProperties(leafState, brightnessLevel),
    [leafState.color, leafState.power, brightnessLevel],
  );

  const { colorRgb, brightnessFactor, powerState, kelvinRGB, rgbColor, colorType, temperatureState } = colorProperties;

  // if either value is present ...
  const colorStyle = useMemo(
    () => (colorType === 'rgb' ? rgbToString(rgbColor) : rgbToString(kelvinRGB as RGBColor)),
    [colorType, rgbColor, kelvinRGB],
  );

  const createGradient = useMemo(
    () => (baseColor: string) => {
      if (colorType === 'rgb') {
        const { shadowColor, dimmedColor } = createRgbGradient(baseColor, brightnessFactor);
        return RGB_GRADIENT_TEMPLATE(shadowColor, dimmedColor);
      }
      if (colorType === 'temperature') {
        const { blendedColor, baseColor: tempBaseColor } = createTempGradient(
          baseColor,
          temperatureState || DEFAULT_COLOR.temperature,
          brightnessFactor,
          brightnessLevel,
        );
        return TEMP_GRADIENT_TEMPLATE(blendedColor, tempBaseColor);
      }
    },
    [colorType, brightnessFactor, temperatureState, brightnessLevel],
  );

  const backgroundTile = useMemo(() => {
    if (powerState === 'on') {
      return createGradient(colorStyle);
    }
    return POWER_OFF_GRADIENT;
  }, [powerState, createGradient, colorStyle]);

  return (
    <Accordion
      className={styles.accordion}
      key={leaf.id}
      expanded={isExpanded}
      onChange={() => setIsExpanded(!isExpanded)}
    >
      <Stack
        direction="column"
        sx={{
          minHeight: 125,
          borderRadius: 4,
          background: backgroundTile,
          color: adjustTextColor(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState, powerState),
          cursor: 'pointer',
          transition: 'background 0.3s ease-in-out, color 0.3s ease-in-out',
          boxShadow: '0 2px 3px rgba(0, 0, 0, 0.5), 0 2px 3px rgba(0, 0, 0, 0.3)',
          justifyContent: 'space-between',
          fontSize: '40px',
        }}
        className={styles.lightTile}
      >
        <Box className={styles.light_title_content}>
          <Box className={styles.light_icon_text_wrapper}>
            <LightbulbCircleSharpIcon fontSize="large" />
            <Typography className={styles.light_text} variant="h3" sx={{ minWidth: '45%' }}>
              <span>{leaf.name}</span>
            </Typography>
          </Box>
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                sx={{
                  color: adjustTextColor(
                    colorRgb,
                    brightnessFactor,
                    brightnessLevel,
                    colorType,
                    temperatureState,
                    powerState,
                  ),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              />
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <LightsPowerSwitch />
          </AccordionSummary>
        </Box>

        <Box className={styles.slider_box}>
          <BrightnessSlider />
        </Box>
      </Stack>
      <AccordionDetails className={styles.accordionDetails}>
        <Box>
          <ControlPanelWrapper />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
